import { DailyQuestion } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

import { skillLabelFor } from './format';
import { letterFor } from './optionLetters';

/** Index of the question's correct option; -1 if none is flagged correct. */
export const correctIndexOf = (question: DailyQuestion): number =>
  question.options.findIndex((option) => option.isCorrect);

/** A skill's tally within a scored quiz. */
export type SkillTally = Readonly<{ skill: string; correct: number; total: number }>;

/** Aggregate score for a set of answered questions, plus the per-skill breakdown. */
export type QuizScore = Readonly<{
  score: number;
  total: number;
  percent: number;
  bySkill: readonly SkillTally[];
}>;

/** One option in the answer-review list, with its letter already derived from position. */
export type ReviewOption = Readonly<{
  id: string;
  letter: string;
  text: string;
  isCorrect: boolean;
  /** Why this option is right or wrong; null when the question carries only an overall note. */
  explanation: string | null;
  mediaUrl: string | null;
}>;

/** One question in the answer-review list. */
export type ReviewItem = Readonly<{
  id: string;
  stem: string;
  /** The question's overall note; null when the per-option reasons stand alone. */
  explanation: string | null;
  options: readonly ReviewOption[];
  /** The student's answer as a letter, or a dash when the question was left blank. */
  yourAnswer: string;
  correctAnswer: string;
  correct: boolean;
}>;

/** One answered question — the shared input both the live and persisted results build from. */
export type AnsweredQuestion = Readonly<{
  question: DailyQuestion;
  /** The chosen option position, or null when left blank. */
  chosenIndex: number | null;
  correct: boolean;
}>;

/** A fully-resolved results view: headline score, per-skill tally, and the review list. */
export type QuizResultModel = QuizScore & Readonly<{ reviewItems: readonly ReviewItem[] }>;

const NO_ANSWER = '—';

const letterOrBlank = (question: DailyQuestion, index: number | null): string =>
  index !== null && question.options[index] ? letterFor(index) : NO_ANSWER;

/**
 * Build the results view from answered questions. Score/total default to the
 * answered set (correct count / length) but can be overridden by the recorded
 * attempt's own totals, so a reconstructed view always shows the number that was
 * actually saved even if a question later left the bank.
 */
export const buildResultModel = (
  answered: readonly AnsweredQuestion[],
  overrides: { score?: number; total?: number } = {},
): QuizResultModel => {
  const perSkill = new Map<string, { correct: number; total: number }>();
  let correctCount = 0;

  const reviewItems = answered.map((entry): ReviewItem => {
    if (entry.correct) correctCount += 1;

    const skill = skillLabelFor(entry.question);
    const tally = perSkill.get(skill) ?? { correct: 0, total: 0 };

    tally.total += 1;
    if (entry.correct) tally.correct += 1;

    perSkill.set(skill, tally);

    return {
      id: entry.question.id,
      stem: entry.question.stem,
      explanation: entry.question.explanation,
      options: entry.question.options.map(
        (option, index): ReviewOption => ({
          id: option.id,
          letter: letterFor(index),
          text: option.text,
          isCorrect: option.isCorrect,
          explanation: option.explanation,
          mediaUrl: option.mediaUrl,
        }),
      ),
      yourAnswer: letterOrBlank(entry.question, entry.chosenIndex),
      correctAnswer: letterOrBlank(entry.question, correctIndexOf(entry.question)),
      correct: entry.correct,
    };
  });

  const score = overrides.score ?? correctCount;
  const total = overrides.total ?? answered.length;

  return {
    score,
    total,
    percent: total > 0 ? Math.round((score / total) * 100) : 0,
    bySkill: Array.from(perSkill, ([skill, tally]) => ({ skill, ...tally })),
    reviewItems,
  };
};

/** Resolve the answered questions for the live quiz from the in-progress answer map. */
export const answeredFromLiveQuiz = (
  questions: readonly DailyQuestion[],
  answers: Readonly<Record<number, number>>,
): AnsweredQuestion[] =>
  questions.map((question, index) => {
    const chosenIndex = answers[index] ?? null;

    return { question, chosenIndex, correct: chosenIndex === correctIndexOf(question) };
  });
